import React from 'react'
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react'

/**
 * Floating notification component for displaying system messages
 * Supports different notification types with appropriate styling and icons
 */

const notificationStyles = {
  error: {
    container: 'bg-red-50 !border !border-red-200 !text-red-800',
    icon: '!text-red-500',
    title: '!text-red-900',
    message: '!text-red-700',
    closeButton: '!text-red-400 hover:!text-red-600'
  },
  success: {
    container: 'bg-green-50 !border !border-green-200 !text-green-800',
    icon: '!text-green-500',
    title: '!text-green-900',
    message: '!text-green-700',
    closeButton: '!text-green-400 hover:!text-green-600'
  },
  warning: {
    container: 'bg-yellow-50 !border !border-yellow-200 !text-yellow-800',
    icon: '!text-yellow-500',
    title: '!text-yellow-900',
    message: '!text-yellow-700',
    closeButton: '!text-yellow-400 hover:!text-yellow-600'
  },
  info: {
    container: 'bg-blue-50 !border !border-blue-200 !text-blue-800',
    icon: '!text-blue-500',
    title: '!text-blue-900',
    message: '!text-blue-700',
    closeButton: '!text-blue-400 hover:!text-blue-600'
  }
}

const notificationIcons = {
  error: AlertCircle,
  success: CheckCircle,
  warning: AlertTriangle,
  info: Info
}

const FloatingNotification = ({ notification, onClose }) => {
  const { id, type, title, message, persistent } = notification
  const styles = notificationStyles[type] || notificationStyles.info
  const IconComponent = notificationIcons[type] || Info

  return (
    <div
      className={`min-w-[320px] max-w-[400px] w-auto border rounded-lg shadow-lg p-3 transform transition-all duration-300 ease-in-out animate-in notification-container ${styles.container}`}
      role="alert"
      aria-live="assertive"
    >
      <div className="flex items-start space-x-3 notification-content">
        {/* Icon */}
        <div className={`flex-shrink-0 ${styles.icon}`}>
          <IconComponent className="w-5 h-5" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 notification-text">
          {title && (
            <h4 className={`text-sm font-medium ${styles.title} mb-1 break-words`}>
              {title}
            </h4>
          )}
          {message && (
            <p className={`text-xs ${styles.message} break-words`}>
              {message}
            </p>
          )}
        </div>

        {/* Close Button */}
        <button
          onClick={() => onClose(id)}
          className={`
            flex-shrink-0 p-1 rounded-md transition-colors
            ${styles.closeButton}
            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-transparent
          `}
          aria-label="Close notification"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Progress bar for non-persistent notifications */}
      {!persistent && (
        <div className="mt-3 w-full bg-gray-200 rounded-full h-1">
          <div 
            className="bg-current h-1 rounded-full notification-progress"
          />
        </div>
      )}
    </div>
  )
}

export default FloatingNotification
